import { SimpleLegalFooter } from "@/components/LegalFooter";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Shield,
  FileText,
  User,
  LogOut,
  Plus,
  Download,
  Eye,
  Clock,
  CheckCircle2,
  AlertCircle,
  Car,
  HeartPulse,
  Home,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Logo TheGoodDeal.fr
const THEGOODDEAL_LOGO = "/images/thegooddeal.jpg";

const insuranceIcons = {
  auto: Car,
  moto: Car,
  sante: HeartPulse,
  multirisque: Home,
};

const statusColors = {
  draft: "bg-slate-100 text-slate-800",
  pending: "bg-yellow-100 text-yellow-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  active: "bg-green-100 text-green-800",
  pending_signature: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
};

const statusLabels = {
  draft: "Brouillon",
  pending: "En attente",
  accepted: "Accepté",
  rejected: "Refusé",
  active: "Actif",
  pending_signature: "En attente de signature",
  cancelled: "Annulé",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [client, setClient] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  
  // Register form
  const [registerData, setRegisterData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const savedClient = localStorage.getItem("client");
    
    if (token && savedClient) {
      setIsLoggedIn(true);
      setClient(JSON.parse(savedClient));
      fetchClientData(JSON.parse(savedClient).id);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchClientData = async (clientId) => {
    try {
      const headers = { "X-Client-Token": localStorage.getItem("authToken") || "" };
      const [quotesRes, contractsRes] = await Promise.all([
        axios.get(`${API}/quotes?client_id=${clientId}`, { headers }),
        axios.get(`${API}/contracts?client_id=${clientId}`, { headers }),
      ]);
      setQuotes(quotesRes.data);
      setContracts(contractsRes.data);
    } catch (err) {
      console.error("Error fetching client data:", err);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      localStorage.setItem("authToken", res.data.token);
      localStorage.setItem("client", JSON.stringify(res.data.client));
      setClient(res.data.client);
      setIsLoggedIn(true);
      setIsLoginOpen(false);
      fetchClientData(res.data.client.id);
      toast.success("Connexion réussie !");
    } catch (err) {
      toast.error("Identifiants incorrects");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem("authToken", res.data.token);
      localStorage.setItem("client", JSON.stringify(res.data.client));
      setClient(res.data.client);
      setIsLoggedIn(true);
      setIsRegisterOpen(false);
      toast.success("Compte créé avec succès !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("client");
    setIsLoggedIn(false);
    setClient(null);
    setQuotes([]);
    setContracts([]);
    toast.success("Déconnexion réussie");
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header - Blue */}
        <header className="bg-[#102692] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                {/* Logo animé avec bordure violette */}
                <motion.div className="relative">
                  <motion.div
                    className="absolute -inset-1 rounded-xl"
                    style={{
                      background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #8B5CF6)",
                    }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  />
                  <div className="relative bg-white rounded-lg p-1">
                    <img src={JYVIS_LOGO} alt="JYVIS Assurances" className="h-10 w-auto" />
                  </div>
                </motion.div>
                <a href="https://thegooddeal.fr" target="_blank" rel="noopener noreferrer" className="hidden sm:block hover:opacity-80 ml-3">
                  <img src={THEGOODDEAL_LOGO} alt="TheGoodDeal.fr" className="h-8 w-auto rounded" />
                </a>
              </Link>
              <Link to="/">
                <Button variant="outline" className="text-white border-white/20 hover:bg-white/10">
                  Retour au site
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Login/Register Section */}
        <div className="max-w-md mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <User className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 mb-2">
              Espace Client
            </h1>
            <p className="text-slate-600">
              Connectez-vous pour accéder à vos contrats et devis
            </p>
          </motion.div>

          <Card data-testid="auth-card">
            <CardContent className="p-6 space-y-4">
              {/* Login Button */}
              <Dialog open={isLoginOpen} onOpenChange={setIsLoginOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-primary hover:bg-primary/90 h-12" data-testid="login-btn">
                    Se connecter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Connexion</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="mt-2"
                        data-testid="login-email"
                        required
                      />
                    </div>
                    <div>
                      <Label>Mot de passe</Label>
                      <Input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="mt-2"
                        data-testid="login-password"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-primary" data-testid="login-submit">
                      Se connecter
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <div className="flex items-center">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="px-4 text-sm text-slate-500">ou</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Register Button */}
              <Dialog open={isRegisterOpen} onOpenChange={setIsRegisterOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full h-12" data-testid="register-btn">
                    Créer un compte
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Créer un compte</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prénom</Label>
                        <Input
                          value={registerData.first_name}
                          onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                          className="mt-2"
                          data-testid="register-firstname"
                          required
                        />
                      </div>
                      <div>
                        <Label>Nom</Label>
                        <Input
                          value={registerData.last_name}
                          onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                          className="mt-2"
                          data-testid="register-lastname"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="mt-2"
                        data-testid="register-email"
                        required
                      />
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <Input
                        type="tel"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        className="mt-2"
                        data-testid="register-phone"
                        required
                      />
                    </div>
                    <div>
                      <Label>Mot de passe</Label>
                      <Input
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="mt-2"
                        data-testid="register-password"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-primary" data-testid="register-submit">
                      Créer mon compte
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <p className="text-center text-sm text-slate-500 pt-4">
                Pas encore de devis ? <Link to="/" className="text-primary hover:underline">Obtenir un devis</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Blue */}
      <header className="bg-[#102692] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              {/* Logo animé avec bordure violette */}
              <motion.div className="relative">
                <motion.div
                  className="absolute -inset-1 rounded-xl"
                  style={{
                    background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #8B5CF6)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative bg-white rounded-lg p-1">
                  <img src={JYVIS_LOGO} alt="JYVIS Assurances" className="h-8 w-auto" />
                </div>
              </motion.div>
              <a href="https://thegooddeal.fr" target="_blank" rel="noopener noreferrer" className="hidden sm:block hover:opacity-80 ml-3">
                <img src={THEGOODDEAL_LOGO} alt="TheGoodDeal.fr" className="h-6 w-auto rounded" />
              </a>
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-white/70">
                Bonjour, <span className="font-semibold text-white">{client?.first_name}</span>
              </span>
              <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="client-dashboard">
        {/* Welcome Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#102692] to-[#2563EB] rounded-2xl p-8 text-white mb-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">
              Bienvenue dans votre espace client
            </h1>
            <p className="text-white/70 mb-6">
              Gérez vos contrats, consultez vos devis et téléchargez vos documents
            </p>
            <Button
              onClick={() => navigate("/devis/auto")}
              className="bg-white hover:bg-white/90 text-primary"
              data-testid="new-quote-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau devis
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="stat-card" data-testid="stat-quotes">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Devis en cours</p>
                  <p className="text-3xl font-bold text-slate-900">{quotes.filter(q => q.status === "pending").length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-contracts">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Contrats actifs</p>
                  <p className="text-3xl font-bold text-slate-900">{contracts.filter(c => c.status === "active").length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">En attente signature</p>
                  <p className="text-3xl font-bold text-slate-900">{contracts.filter(c => c.status === "pending_signature").length}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="contracts" className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="contracts" data-testid="tab-contracts">Mes contrats</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Mes devis</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Mon profil</TabsTrigger>
          </TabsList>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <Card>
              <CardHeader>
                <CardTitle>Mes contrats</CardTitle>
              </CardHeader>
              <CardContent>
                {contracts.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Aucun contrat pour le moment</p>
                    <Button onClick={() => navigate("/devis/auto")} className="bg-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Souscrire une assurance
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contracts.map((contract) => {
                      const Icon = insuranceIcons[contract.insurance_type] || Shield;
                      return (
                        <div
                          key={contract.id}
                          className="flex items-center justify-between p-4 border rounded-xl hover:border-primary/30 transition-colors"
                          data-testid={`contract-${contract.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                              <Icon className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold capitalize">{contract.insurance_type.replace("_", " ")}</p>
                              <p className="text-sm text-slate-500">Réf: {contract.reference}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-primary">{contract.monthly_premium}€/mois</p>
                              <span className={`status-badge ${statusColors[contract.status]}`}>
                                {statusLabels[contract.status]}
                              </span>
                            </div>
                            <Button variant="ghost" size="icon">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Mes devis</CardTitle>
              </CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Aucun devis pour le moment</p>
                    <Button onClick={() => navigate("/devis/auto")} className="bg-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Demander un devis
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {quotes.map((quote) => {
                      const Icon = insuranceIcons[quote.insurance_type] || Shield;
                      return (
                        <div
                          key={quote.id}
                          className="flex items-center justify-between p-4 border rounded-xl hover:border-primary/30 transition-colors"
                          data-testid={`quote-${quote.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                              <Icon className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold capitalize">{quote.insurance_type.replace("_", " ")}</p>
                              <p className="text-sm text-slate-500">Réf: {quote.reference}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-primary">{quote.monthly_premium}€/mois</p>
                              <span className={`status-badge ${statusColors[quote.status]}`}>
                                {statusLabels[quote.status]}
                              </span>
                            </div>
                            <Button variant="ghost" size="icon">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Mon profil</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-500">Prénom</Label>
                    <p className="font-medium">{client?.first_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Nom</Label>
                    <p className="font-medium">{client?.last_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email</Label>
                    <p className="font-medium">{client?.email}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Téléphone</Label>
                    <p className="font-medium">{client?.phone}</p>
                  </div>
                  {client?.address && (
                    <div className="md:col-span-2">
                      <Label className="text-slate-500">Adresse</Label>
                      <p className="font-medium">{client?.address}, {client?.postal_code} {client?.city}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <SimpleLegalFooter />
    </div>
  );
}
